import { find } from 'lodash';
import { stringify } from 'qs';
import { getConfig } from '../configProvider';
import { getEnvPrefix } from '../utils/env';
import pathMappings from './constants';

/**
 * 获取指定localStorage key 是否存在
 */
export const canShow = (key: string) => {
    return localStorage.getItem(key) !== 'true';
};
/**
 * 写入 localStorage
 */
export const setDataInStorage = (key: string) => {
    localStorage.setItem(key, 'true');
};

/**
 * 写入 banner 唤醒时间
 */
export const setBannerShowTimer = (key: string) => {
    const expiredTime = new Date().getTime();
    localStorage.setItem(key, String(expiredTime));
};

/**
 * 判断 banner 是否还可以展示，过期时间 3 个月
 */
export const canShowBanner = (key: string) => {
    const expiredTime = localStorage.getItem(key);
    if (!expiredTime) return true;
    // 3个月过期时间，如果 banner 唤醒时间 + 3 个月大于当前时间，则可以展示

    return Number(expiredTime) + 3 * 30 * 24 * 60 * 60 * 1000 > new Date().getTime();
};

export const getTargetData = (currentPath: string) => {
    return find(
        pathMappings,
        (item) => item.accessPath === currentPath || currentPath.startsWith(item.accessPath + '/'),
    );
};
/**
 * 获取目标路径映射数据
 * @param currentPath
 * @returns
 */
export const getTargetPath = (currentPath: string) => {
    const mapping = getTargetData(currentPath);
    return mapping?.targetPath || undefined;
};

const getServerJumpURL = (url: string) => {
    return `https://${getEnvPrefix(
        getConfig().env,
    )}webapi.designkit.com/v1/designkitcn/redirect?redirect_url=${url}&referrer=${document.referrer}`;
};

/**
 * 获取跳转目标连接
 */
export const getTargetsUrl = (targetPath: string) => {
    const { env, appName, isIntl } = getConfig();

    const isDesignkit = appName === 'designkit' && isIntl;
    // 避免和其他的 key 冲突
    const query = {
        came_from: isDesignkit ? 'designkit' : 'xdesign',
        referrer: document.referrer,
    };
    const prefix = env === 'release' ? 'www' : env;
    const url = `https://${prefix}.designkit.cn${targetPath}?${stringify(query)}`;
    return getServerJumpURL(url);
};

/**
 * 获取跳转目标连接
 */
export const rebuildForceTargetUrl = (targetUrl: string) => {
    const { appName, isIntl } = getConfig();

    const isDesignkit = appName === 'designkit' && isIntl;

    const extraQuery = {
        came_from: isDesignkit ? 'designkit' : 'xdesign',
        referrer: document.referrer,
    };

    const url = new URL(targetUrl);

    // 合并 query（不覆盖已有的同名参数）
    Object.entries(extraQuery).forEach(([key, value]) => {
        if (value && !url.searchParams.has(key)) {
            url.searchParams.set(key, value);
        }
    });

    return getServerJumpURL(url.toString());
};

/**
 * 格式化path
 */
export const formatPath = (path: string) => {
    // 小写
    let newPath = path.toLowerCase();
    // 去掉末尾的斜杠
    return path !== '/' && newPath.endsWith('/') ? newPath.slice(0, -1) : newPath;
};

/**
 * 弹窗曝光
 * @param track
 * @param pageName
 */
export const trackPopupShow = ({
    track,
    pageName,
    isBanner,
    popupType,
    popupName,
}: {
    track: (name: string, option: Record<string, any>) => void;
    pageName?: string;
    popupType?: string;
    popupName?: string;
    isBanner?: boolean;
}) => {
    const targetData = getTargetData(formatPath(window.location.pathname));
    if (!targetData) return;
    track('design_popup_show', {
        page_name: pageName || targetData.pageName,
        popup_type: popupType || isBanner ? 'redirect_banner' : 'redirect',
        popup_name: popupName || 'redirect_domestic',
    });
};

/**
 * 弹窗点击
 * @param track
 * @param isJump
 * @param pageName
 */
export const trackPopupClick = ({
    track,
    isJump,
    pageName,
    popupType,
    popupName,
    isBanner,
}: {
    track: (name: string, option: Record<string, any>) => void;
    isJump: boolean;
    pageName?: string;
    popupType?: string;
    popupName?: string;
    isBanner?: boolean;
}) => {
    const targetData = getTargetData(formatPath(window.location.pathname));
    if (!targetData) return;

    track('design_popup_click', {
        page_name: pageName || targetData.pageName,
        popup_type: popupType || isBanner ? 'redirect_banner' : 'redirect',
        popup_name: popupName || 'redirect_domestic',
        click_type: isJump ? 1 : 0,
    });
};

export const removeUrlParameterFromAddressBar = (params: string[]) => {
    const url = new URL(window.location.href);

    params.forEach((p) => {
        url.searchParams.delete(p);
    });

    window.history.replaceState(null, '', url.toString());
};

export const getQueryParam = (key: string) => {
    try {
        return new URLSearchParams(decodeURIComponent(window.location.search)).get(key) || '';
    } catch (e) {
        const reg = new RegExp(`[?&]${key}=([^&]*)`);
        const search = decodeURIComponent(window.location.search);
        const match = search.match(reg);
        return (match ? match[1] : null) || '';
    }
};
